/**
 * Service for handling license key formatting and validation
 */
export class LicenseFormattingService {
  /**
   * Format license key input with proper structure
   * @param input - Raw license key input
   * @returns Formatted license key string
   */
  static formatLicenseKeyInput(input: string): string {
    const upperInput = input.toUpperCase();

    // Check if it's a BETA key - don't reformat, just clean
    if (upperInput.startsWith('BETA-')) {
      // Keep BETA keys as-is, just remove invalid characters
      return input.replace(/[^A-Za-z0-9-]/g, '').toUpperCase();
    }

    // For regular keys: Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    // Split into groups of 8 characters
    const groups: string[] = [];
    for (let i = 0; i < cleaned.length && i < 32; i += 8) {
      groups.push(cleaned.substring(i, Math.min(i + 8, cleaned.length)));
    }

    // Join with hyphens
    return groups.join('-');
  }

  /**
   * Check if cursor position needs adjustment after formatting
   * @param oldValue - Previous input value
   * @param newValue - New formatted value
   * @param oldPosition - Previous cursor position
   * @returns New cursor position
   */
  static calculateCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // Count how many valid characters were before the cursor
    const validCharsBefore = oldValue.substring(0, oldPosition).replace(/[^A-Z0-9]/gi, '').length;
    
    // Find the new position based on valid characters
    let newPosition = 0;
    let validCharCount = 0;
    
    for (let i = 0; i < newValue.length; i++) {
      if (newValue[i] !== '-') {
        validCharCount++;
      }
      if (validCharCount === validCharsBefore) {
        newPosition = i + 1;
        break;
      }
    }
    
    // If we're at the end of a group and there's a hyphen next, skip it
    if (newValue[newPosition] === '-') {
      newPosition++;
    }
    
    return newPosition;
  }

  /**
   * Validate if license key has correct format
   * @param licenseKey - License key to validate
   * @returns True if valid format
   */
  static isValidFormat(licenseKey: string): boolean {
    // Check for BETA key format: BETA-{userId}-{expiryDate}-{signature} or BETA-{userId}-{signature}
    if (licenseKey.toUpperCase().startsWith('BETA-')) {
      // BETA keys should have at least BETA-userId-signature format
      const parts = licenseKey.split('-');
      const isValid = parts.length >= 3 && parts[0].toUpperCase() === 'BETA';
      return isValid;
    }

    // Regular license key: 4 groups of 8 alphanumeric characters separated by hyphens
    // Case-insensitive to support both uppercase and lowercase (Gumroad uses mixed case)
    const pattern = /^[A-Za-z0-9]{8}-[A-Za-z0-9]{8}-[A-Za-z0-9]{8}-[A-Za-z0-9]{8}$/;
    const isValid = pattern.test(licenseKey);
    return isValid;
  }
}